import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  isOnboardingEligible,
  onboardingKeys,
  useOnboardingSettings,
} from '../../hooks/useOnboarding';
import { OnboardingWizard } from './OnboardingWizard';
import { OnboardingRootRouteGuard } from './OnboardingRootRouteGuard';
import { consumeOpenOnboardingFromInvite } from './onboardingInviteStorage';
import {
  canVoluntarilyReopenOnboardingWizard,
  isOnboardingWizardGateEnabled,
  shouldAutoOpenOnboardingWizard,
} from './onboardingGate';
import { onboardingBelowHeaderBackgroundSx } from './onboardingFlowBackground';
import AppConfig from '../../AppConfig';
import {
  collapseAtlasGlobalNavPanel,
  expandAtlasGlobalNavPanel,
} from '../GlobalNav/atlasGlobalNavPanel';

interface OnboardingWizardContextValue {
  openWizard: () => void;
  isWizardOpen: boolean;
  isResolvingMandatoryOnboarding: boolean;
  requiresRootOnboardingRoute: boolean;
}

const OnboardingWizardContext = createContext<OnboardingWizardContextValue | null>(null);

export function useOnboardingWizardControl(): OnboardingWizardContextValue {
  const context = useContext(OnboardingWizardContext);
  if (!context) {
    return {
      openWizard: () => {},
      isWizardOpen: false,
      isResolvingMandatoryOnboarding: false,
      requiresRootOnboardingRoute: false,
    };
  }
  return context;
}

interface OnboardingWizardProviderProps {
  children: ReactNode;
}

/**
 * Hosts onboarding wizard: mandatory flow locks `/` and blocks other routes;
 * voluntary reopen (Courses banner) also opens at `/` but dismisses when the user navigates away.
 */
export function OnboardingWizardProvider({ children }: OnboardingWizardProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { profileData, appAccountType, isLoading: isProfileLoading } = useUserProfile();
  const orgId = profileData?.orgId;
  const userId = profileData?.userId;
  const organizationAppAccountType = profileData?.organization?.appAccountType;

  const gateEnabled = isOnboardingWizardGateEnabled(
    orgId,
    isProfileLoading,
    organizationAppAccountType,
  );

  const {
    data: settings,
    isLoading: isSettingsLoading,
    isSuccess: isSettingsSuccess,
    isError: isSettingsError,
  } = useOnboardingSettings(orgId, gateEnabled);

  const [manualOpen, setManualOpen] = useState(false);
  const [openFromInvite, setOpenFromInvite] = useState(false);
  const [autoOpenSuppressed, setAutoOpenSuppressed] = useState(false);
  /** Path where voluntary wizard is anchored; dismiss only when user leaves this route. */
  const voluntaryWizardAnchorPathRef = useRef<string | null>(null);
  /** Target path while openWizard navigates (avoids closing before navigation settles). */
  const voluntaryWizardPendingAnchorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!gateEnabled || !isSettingsSuccess) {
      return;
    }

    if (consumeOpenOnboardingFromInvite()) {
      setOpenFromInvite(true);
      setAutoOpenSuppressed(false);
    }
  }, [gateEnabled, isSettingsSuccess, orgId]);

  const shouldAutoOpen = useMemo(() => {
    if (!gateEnabled || isSettingsLoading || !isSettingsSuccess || autoOpenSuppressed) {
      return false;
    }

    return shouldAutoOpenOnboardingWizard({
      settings,
      userId,
      appAccountType: organizationAppAccountType ?? appAccountType,
      openFromInvite,
    });
  }, [
    appAccountType,
    organizationAppAccountType,
    autoOpenSuppressed,
    gateEnabled,
    isSettingsLoading,
    isSettingsSuccess,
    openFromInvite,
    settings,
    userId,
  ]);

  const isResolvingMandatoryOnboarding =
    gateEnabled &&
    (isProfileLoading ||
      isSettingsLoading ||
      (!isSettingsSuccess && !isSettingsError));

  const isMandatoryOnboarding = shouldAutoOpen;
  const isWizardOpen = manualOpen || isMandatoryOnboarding;
  /** Hide app routes whenever the wizard is open so voluntary setup uses `/` only (not Courses overlay). */
  const hideAppContent = isWizardOpen;

  useEffect(() => {
    const layoutMain = document.querySelector<HTMLElement>('[data-atlas-gn-app="true"]');
    if (!layoutMain) {
      return;
    }

    if (isWizardOpen) {
      layoutMain.style.overflow = 'hidden';
    } else {
      layoutMain.style.overflow = '';
    }

    return () => {
      layoutMain.style.overflow = '';
    };
  }, [isWizardOpen]);

  /** Full-page onboarding: collapse deployed Atlas side nav while the wizard is open. */
  useEffect(() => {
    if (!isWizardOpen || AppConfig.isLocal) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 30;
    const tryCollapse = () => {
      if (collapseAtlasGlobalNavPanel() || attempts >= maxAttempts) {
        return;
      }
      attempts += 1;
      window.setTimeout(tryCollapse, 100);
    };
    tryCollapse();

    return () => {
      expandAtlasGlobalNavPanel();
    };
  }, [isWizardOpen]);

  const closeWizard = useCallback(() => {
    setManualOpen(false);
    setOpenFromInvite(false);
    setAutoOpenSuppressed(true);
    voluntaryWizardAnchorPathRef.current = null;
    voluntaryWizardPendingAnchorRef.current = null;
  }, []);

  const openWizard = useCallback(() => {
    const accountType = organizationAppAccountType ?? appAccountType;
    const canOpen =
      isOnboardingEligible(settings?.onboardingEligible, accountType) ||
      canVoluntarilyReopenOnboardingWizard({ settings, appAccountType: accountType });

    if (!gateEnabled || !canOpen) {
      return;
    }

    setAutoOpenSuppressed(false);
    setManualOpen(true);

    if (location.pathname === '/') {
      voluntaryWizardPendingAnchorRef.current = null;
      voluntaryWizardAnchorPathRef.current = '/';
      return;
    }

    voluntaryWizardPendingAnchorRef.current = '/';
    voluntaryWizardAnchorPathRef.current = null;
    navigate('/', { replace: true });
  }, [
    appAccountType,
    gateEnabled,
    location.pathname,
    navigate,
    organizationAppAccountType,
    settings,
  ]);

  // Voluntary setup (e.g. Courses banner): dismiss when user navigates away from `/`.
  useEffect(() => {
    if (!manualOpen) {
      voluntaryWizardAnchorPathRef.current = null;
      voluntaryWizardPendingAnchorRef.current = null;
      return;
    }

    // Mandatory onboarding owns routing; do not clear voluntary anchors while pending navigation.
    if (isMandatoryOnboarding) {
      return;
    }

    const pendingAnchor = voluntaryWizardPendingAnchorRef.current;
    if (pendingAnchor !== null) {
      if (location.pathname === pendingAnchor) {
        voluntaryWizardAnchorPathRef.current = location.pathname;
        voluntaryWizardPendingAnchorRef.current = null;
      }
      return;
    }

    const anchorPath = voluntaryWizardAnchorPathRef.current;
    if (anchorPath === null) {
      return;
    }

    if (location.pathname !== anchorPath) {
      closeWizard();
    }
  }, [closeWizard, isMandatoryOnboarding, location.pathname, manualOpen]);

  const requiresRootOnboardingRoute = isMandatoryOnboarding;

  // Guard is not unmounted when app routes are hidden; also force `/` for global-nav clicks.
  useEffect(() => {
    if (!requiresRootOnboardingRoute || location.pathname === '/') {
      return;
    }
    navigate('/', { replace: true });
  }, [location.pathname, navigate, requiresRootOnboardingRoute]);

  const navigateAfterOnboarding = useCallback(async () => {
    if (orgId) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: onboardingKeys.settings(orgId) }),
        queryClient.invalidateQueries({ queryKey: onboardingKeys.bannerStatus(orgId) }),
      ]);
      await queryClient.refetchQueries({ queryKey: onboardingKeys.settings(orgId) });
    }
    // Land on `/` so DefaultRedirect applies the same role-based home as post-login.
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, orgId, queryClient]);

  const handleComplete = useCallback(() => {
    closeWizard();
    void navigateAfterOnboarding();
  }, [closeWizard, navigateAfterOnboarding]);

  const showOnboardingShell = isWizardOpen;

  const contextValue = useMemo(
    () => ({
      openWizard,
      isWizardOpen,
      isResolvingMandatoryOnboarding,
      requiresRootOnboardingRoute,
    }),
    [isResolvingMandatoryOnboarding, isWizardOpen, openWizard, requiresRootOnboardingRoute],
  );

  return (
    <OnboardingWizardContext.Provider value={contextValue}>
      <OnboardingRootRouteGuard />
      {!hideAppContent ? children : null}
      {showOnboardingShell ? (
        <Box aria-hidden sx={onboardingBelowHeaderBackgroundSx} />
      ) : null}
      {gateEnabled && isWizardOpen && orgId ? (
        <OnboardingWizard
          open
          transparentBackdrop
          onClose={closeWizard}
          onComplete={handleComplete}
          orgId={orgId}
        />
      ) : null}
    </OnboardingWizardContext.Provider>
  );
}
